import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import projectService from '../services/projectService';
import { notify } from '../components/CustomToast';

const ProjectContext = createContext();

export const useProject = () => useContext(ProjectContext);

export const ProjectProvider = ({ children }) => {
    // Projects state
    const [projects, setProjects] = useState([]);
    const [activeProjectId, setActiveProjectId] = useState(null);
    const [activeProject, setActiveProject] = useState(null);

    // Tasks state
    const [tasks, setTasks] = useState([]);
    const [activeTaskId, setActiveTaskId] = useState(null);
    const [activeTask, setActiveTask] = useState(null);

    // Comments and attachments
    const [comments, setComments] = useState([]);
    const [attachments, setAttachments] = useState([]);

    // Loading states
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingTask, setIsLoadingTask] = useState(false);
    const [loadingProjectId, setLoadingProjectId] = useState(null); // Track which project is loading

    // Dashboard state
    const [dashboardStats, setDashboardStats] = useState(null);
    const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
    const isFetchingDashboard = useRef(false);

    // Filter state
    const [filters, setFilters] = useState({
        teamId: null,
        status: null,
        taskStatus: null,
        taskPriority: null,
    });

    // Refs to prevent duplicate API calls
    const isInitialized = useRef(false);
    const lastProjectId = useRef(null);
    const lastTaskId = useRef(null);

    // Fetch dashboard stats
    const fetchDashboardStats = useCallback(async () => {
        if (isFetchingDashboard.current) return; // Prevent duplicate calls
        isFetchingDashboard.current = true;
        setIsLoadingDashboard(true);
        try {
            const response = await projectService.getDashboardStats();
            setDashboardStats(response?.data);
        } catch (error) {
            console.error('Failed to load dashboard stats', error);
        } finally {
            isFetchingDashboard.current = false;
            setIsLoadingDashboard(false);
        }
    }, []);

    // Fetch all projects
    const fetchProjects = useCallback(async (teamId = null, status = null) => {
        try {
            setIsLoading(true);
            const params = {};
            if (teamId) params.team_id = teamId;
            if (status) params.status = status;

            const response = await projectService.getProjects(params);
            const projectList = response?.data?.projects || [];
            setProjects(projectList);

            // Removed auto-select first project to allow showing dashboard
        } catch (error) {
            console.error('Failed to load projects', error);
            notify('error', 'Failed to load projects');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch single project with tasks
    const fetchProject = useCallback(async (projectId) => {
        if (!projectId) return;

        try {
            setIsLoading(true);
            const response = await projectService.getProject(projectId);
            const project = response?.data?.project;
            setActiveProject(project);

            // Set tasks from the project response
            if (project?.tasks) {
                setTasks(project.tasks);
            }

            return project;
        } catch (error) {
            console.error('Failed to load project', error);
            notify('error', 'Failed to load project');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch tasks for a project
    const fetchTasks = useCallback(async (projectId, queryParams = {}) => {
        if (!projectId) return;

        try {
            // If explicit loading state check is needed, it can be added here
            const response = await projectService.getTasks(projectId, queryParams);
            const taskList = response?.data?.tasks || [];

            // If using pagination (queryParams.per_page exists and pagination metadata is returned), 
            // you might want to store pagination data too, but current structure just sets tasks.
            // Assuming the simple case for now or backward compatibility.
            // If paginated response structure change (tasks inside 'data' vs 'tasks'), 
            // the service handles it or existing logic holds.
            // Standard API response in controller checks perPage.

            // If pagination is used, the structure changes to { tasks: [], pagination: {} }
            // If just get() is used, it's { tasks: [] }

            // For now, let's assume we handle the tasks array extraction safely
            // If per_page is sent, response.data.tasks is the array (from the controller resource collection)
            setTasks(taskList);
            return response?.data; // Return full data for pagination details if needed
        } catch (error) {
            console.error('Failed to load tasks', error);
        }
    }, []);

    // Select a project
    const selectProject = useCallback(async (projectId) => {
        // Set loading state immediately for instant feedback
        setLoadingProjectId(projectId);
        setActiveProjectId(projectId);
        setActiveTaskId(null);
        setActiveTask(null);
        setComments([]);
        setAttachments([]);

        // Clear project and tasks immediately to show skeleton loader
        setActiveProject(null);
        setTasks([]);

        if (projectId) {
            try {
                await fetchProject(projectId);
            } finally {
                setLoadingProjectId(null);
            }
        } else {
            setLoadingProjectId(null);
        }
    }, [fetchProject, fetchDashboardStats]);

    // Fetch single task with details
    const fetchTask = useCallback(async (taskId) => {
        if (!taskId) return;

        try {
            setIsLoadingTask(true);
            const response = await projectService.getTask(taskId);
            const task = response?.data?.task;
            setActiveTask(task);

            // Set comments and attachments from task
            setComments(task?.comments || []);
            setAttachments(task?.attachments || []);

            return task;
        } catch (error) {
            console.error('Failed to load task', error);
            notify('error', 'Failed to load task');
        } finally {
            setIsLoadingTask(false);
        }
    }, []);

    // Select a task
    const selectTask = useCallback(async (taskId) => {
        setActiveTaskId(taskId);
        if (taskId) {
            await fetchTask(taskId);
        } else {
            setActiveTask(null);
            setComments([]);
            setAttachments([]);
        }
    }, [fetchTask]);

    // Create project
    const createProject = useCallback(async (data) => {
        try {
            const response = await projectService.createProject(data);
            const newProject = response?.data?.project;

            if (newProject) {
                setProjects(prev => [newProject, ...prev]);
                notify('success', 'Project created successfully');
            }

            return newProject;
        } catch (error) {
            console.error('Failed to create project', error);
            notify('error', 'Failed to create project');
            throw error;
        }
    }, []);

    // Update project
    const updateProject = useCallback(async (projectId, data) => {
        try {
            const response = await projectService.updateProject(projectId, data);
            const updatedProject = response?.data?.project;

            if (updatedProject) {
                setProjects(prev => prev.map(p =>
                    p.id === projectId ? updatedProject : p
                ));
                if (activeProjectId === projectId) {
                    setActiveProject(updatedProject);
                }
                notify('success', 'Project updated successfully');
            }

            return updatedProject;
        } catch (error) {
            console.error('Failed to update project', error);
            notify('error', 'Failed to update project');
            throw error;
        }
    }, [activeProjectId]);

    // Delete project
    const deleteProject = useCallback(async (projectId) => {
        try {
            await projectService.deleteProject(projectId);
            setProjects(prev => prev.filter(p => p.id !== projectId));

            if (activeProjectId === projectId) {
                setActiveProjectId(null);
                setActiveProject(null);
                setTasks([]);
            }

            notify('delete', 'Project deleted successfully');
        } catch (error) {
            console.error('Failed to delete project', error);
            notify('error', 'Failed to delete project');
            throw error;
        }
    }, [activeProjectId]);

    // Create task
    const createTask = useCallback(async (projectId, data) => {
        try {
            const response = await projectService.createTask(projectId, data);
            const newTask = response?.data?.task;

            if (newTask) {
                // Add task to the tasks list
                setTasks(prev => [newTask, ...prev]);

                // Update activeProject tasks array
                setActiveProject(prev => {
                    if (prev && prev.id === projectId) {
                        return {
                            ...prev,
                            tasks: [newTask, ...(prev.tasks || [])]
                        };
                    }
                    return prev;
                });

                // Update project tasks count in the list
                setProjects(prev => prev.map(p =>
                    p.id === projectId ? { ...p, tasks_count: (p.tasks_count || 0) + 1 } : p
                ));

                notify('success', 'Task created successfully');
            }

            return newTask;
        } catch (error) {
            console.error('Failed to create task', error);
            notify('error', 'Failed to create task');
            throw error;
        }
    }, []);

    // Update task (full update)
    const updateTask = useCallback(async (taskId, data) => {
        try {
            const response = await projectService.updateTask(taskId, data);
            const updatedTask = response?.data?.task;

            if (updatedTask) {
                setTasks(prev => prev.map(t =>
                    t.id === taskId ? { ...t, ...updatedTask } : t
                ));
                if (activeTaskId === taskId) {
                    setActiveTask(prev => ({ ...prev, ...updatedTask }));
                }
                notify('success', 'Task updated successfully');
            }

            return updatedTask;
        } catch (error) {
            console.error('Failed to update task', error);
            notify('error', 'Failed to update task');
            throw error;
        }
    }, [activeTaskId]);

    // Update task status
    const updateTaskStatus = useCallback(async (taskId, status) => {
        try {
            const response = await projectService.updateTaskStatus(taskId, status);
            const updatedTask = response?.data?.task;

            if (updatedTask) {
                setTasks(prev => prev.map(t =>
                    t.id === taskId ? { ...t, status: updatedTask.status } : t
                ));
                if (activeTaskId === taskId) {
                    setActiveTask(prev => ({ ...prev, status: updatedTask.status }));
                }
                notify('success', 'Task status updated');


            }

            return updatedTask;
        } catch (error) {
            console.error('Failed to update task status', error);
            notify('error', 'Failed to update task status');
            throw error;
        }
    }, [activeTaskId, fetchDashboardStats]);

    // Update task priority
    const updateTaskPriority = useCallback(async (taskId, priority) => {
        try {
            const response = await projectService.updateTaskPriority(taskId, priority);
            const updatedTask = response?.data?.task;

            if (updatedTask) {
                setTasks(prev => prev.map(t =>
                    t.id === taskId ? { ...t, priority: updatedTask.priority } : t
                ));
                if (activeTaskId === taskId) {
                    setActiveTask(prev => ({ ...prev, priority: updatedTask.priority }));
                }
                notify('success', 'Task priority updated');
            }

            return updatedTask;
        } catch (error) {
            console.error('Failed to update task priority', error);
            notify('error', 'Failed to update task priority');
            throw error;
        }
    }, [activeTaskId]);

    // Delete task
    const deleteTask = useCallback(async (taskId) => {
        try {
            await projectService.deleteTask(taskId);
            setTasks(prev => prev.filter(t => t.id !== taskId));

            // Update project tasks count
            if (activeProject) {
                setProjects(prev => prev.map(p =>
                    p.id === activeProject.id ? { ...p, tasks_count: Math.max(0, (p.tasks_count || 0) - 1) } : p
                ));
            }

            if (activeTaskId === taskId) {
                setActiveTaskId(null);
                setActiveTask(null);
            }

            notify('delete', 'Task deleted successfully');
        } catch (error) {
            console.error('Failed to delete task', error);
            notify('error', 'Failed to delete task');
            throw error;
        }
    }, [activeTaskId, activeProject]);

    // Create subtask
    const createSubtask = useCallback(async (parentTaskId, data) => {
        try {
            const response = await projectService.createSubtask(parentTaskId, data);
            const newSubtask = response?.data?.task;

            if (newSubtask) {
                // Update parent task's subtasks array and count (don't add to root level)
                setTasks(prev => prev.map(t =>
                    t.id === parentTaskId
                        ? {
                            ...t,
                            subtasks_count: (t.subtasks_count || 0) + 1,
                            subtasks: [...(t.subtasks || []), newSubtask],
                          }
                        : t
                ));

                notify('success', 'Subtask created successfully');
            }

            return newSubtask;
        } catch (error) {
            console.error('Failed to create subtask', error);
            notify('error', 'Failed to create subtask');
            throw error;
        }
    }, []);

    // Get subtasks for a parent task
    const getSubtasks = useCallback(async (parentTaskId) => {
        try {
            const response = await projectService.getSubtasks(parentTaskId);
            return response?.data?.subtasks || [];
        } catch (error) {
            console.error('Failed to fetch subtasks', error);
            return [];
        }
    }, []);

    // Add comment
    const addComment = useCallback(async (taskId, message) => {
        try {
            const response = await projectService.addComment(taskId, message);
            const newComment = response?.data?.comment;

            if (newComment) {
                setComments(prev => [...prev, newComment]);
                // Update task comment count
                setTasks(prev => prev.map(t =>
                    t.id === taskId ? { ...t, comments_count: (t.comments_count || 0) + 1 } : t
                ));
            }

            return newComment;
        } catch (error) {
            console.error('Failed to add comment', error);
            notify('error', 'Failed to add comment');
            throw error;
        }
    }, []);

    // Delete comment
    const deleteComment = useCallback(async (taskId, commentId) => {
        try {
            await projectService.deleteComment(taskId, commentId);
            setComments(prev => prev.filter(c => c.id !== commentId));
            // Update task comment count
            setTasks(prev => prev.map(t =>
                t.id === taskId ? { ...t, comments_count: Math.max(0, (t.comments_count || 0) - 1) } : t
            ));
        } catch (error) {
            console.error('Failed to delete comment', error);
            notify('error', 'Failed to delete comment');
            throw error;
        }
    }, []);

    // Receive real-time comment (from Pusher)
    const receiveRealtimeComment = useCallback((comment) => {
        // Check if comment already exists to avoid duplicates
        setComments(prev => {
            const exists = prev.some(c => c.id === comment.id);
            if (exists) return prev;
            return [...prev, comment];
        });
        // Update task comment count
        setTasks(prev => prev.map(t =>
            t.id === comment.task_id ? { ...t, comments_count: (t.comments_count || 0) + 1 } : t
        ));
    }, []);

    // Remove real-time comment (from Pusher)
    const removeRealtimeComment = useCallback((commentId, taskId) => {
        setComments(prev => prev.filter(c => c.id !== commentId));
        // Update task comment count
        setTasks(prev => prev.map(t =>
            t.id === taskId ? { ...t, comments_count: Math.max(0, (t.comments_count || 0) - 1) } : t
        ));
    }, []);

    // Add attachment
    const addAttachment = useCallback(async (taskId, uploadId) => {
        try {
            const response = await projectService.addAttachment(taskId, uploadId);
            const newAttachment = response?.data?.attachment;

            if (newAttachment) {
                setAttachments(prev => [...prev, newAttachment]);
                // Update task attachment count
                setTasks(prev => prev.map(t =>
                    t.id === taskId ? { ...t, attachments_count: (t.attachments_count || 0) + 1 } : t
                ));
                notify('success', 'File attached successfully');
            }

            return newAttachment;
        } catch (error) {
            console.error('Failed to add attachment', error);
            notify('error', 'Failed to add attachment');
            throw error;
        }
    }, []);

    // Delete attachment
    const deleteAttachment = useCallback(async (taskId, attachmentId) => {
        try {
            await projectService.deleteAttachment(taskId, attachmentId);
            setAttachments(prev => prev.filter(a => a.id !== attachmentId));
            // Update task attachment count
            setTasks(prev => prev.map(t =>
                t.id === taskId ? { ...t, attachments_count: Math.max(0, (t.attachments_count || 0) - 1) } : t
            ));
            notify('delete', 'Attachment removed');
        } catch (error) {
            console.error('Failed to delete attachment', error);
            notify('error', 'Failed to delete attachment');
            throw error;
        }
    }, []);

    // Refresh projects
    const refreshProjects = useCallback(() => {
        fetchProjects(filters.teamId, filters.status);
    }, [fetchProjects, filters]);

    // Refresh tasks
    const refreshTasks = useCallback(() => {
        if (activeProjectId) {
            fetchTasks(activeProjectId, filters.taskStatus, filters.taskPriority);
        }
    }, [activeProjectId, fetchTasks, filters]);

    const value = {
        // State
        projects,
        activeProjectId,
        activeProject,
        tasks,
        activeTaskId,
        activeTask,
        comments,
        attachments,
        dashboardStats,
        isLoading,
        isLoadingTask,
        isLoadingDashboard,
        loadingProjectId,
        filters,

        // Setters
        setFilters,
        setActiveProjectId,

        // Project actions
        fetchProjects,
        fetchDashboardStats,
        fetchProject,
        selectProject,
        createProject,
        updateProject,
        deleteProject,
        refreshProjects,

        // Task actions
        fetchTasks,
        fetchTask,
        selectTask,
        createTask,
        updateTask,
        updateTaskStatus,
        updateTaskPriority,
        deleteTask,
        refreshTasks,

        // Subtask actions
        createSubtask,
        getSubtasks,

        // Comment actions
        addComment,
        deleteComment,
        receiveRealtimeComment,
        removeRealtimeComment,

        // Attachment actions
        addAttachment,
        deleteAttachment,
    };

    return (
        <ProjectContext.Provider value={value}>
            {children}
        </ProjectContext.Provider>
    );
};
